import { useState, FormEvent, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import IconButton from './components/IconButton';
import TrashActionIcon from './components/TrashActionIcon';
import FullScreenConfirmOverlay from './components/FullScreenConfirmOverlay';
import { showToastSuccess, showToastError } from './components/ToastNotifications';
import {
  addCoaFile,
  deleteCoaFile,
  getCoaFilesByLabel,
  setCoaFileDisplayOrder,
  setCoaFileVisibility,
} from './hooks/usePackages';
import { getFileLink, uploadFileId } from './hooks/useFiles';
import { formatDate } from './utils/formatDate';
import { getErrorBody } from './utils/requestErrors';
import { FileType } from './wysiwyg/assetService';
import {
  buildVisibilityRequests,
  compositeKey,
  extractMutationResults,
  formatPrintRunRange,
  groupCoaFiles,
  mutationKey,
  mutationSummary,
  normalizeErrorBody,
  packageGroupKey,
  packageLabelFor,
  pendingCoaFileKey,
  type CoaDropLanding,
  type CoaFileEntry,
  type CoaFileGroup,
  type CoaFileMutationResult,
  type PrintRunEntry,
} from './CoaVisibilityPage.helpers';

function readErrorMessage(error: unknown, fallback: string): string {
  const bodyMessage = normalizeErrorBody(getErrorBody(error));
  if (bodyMessage) return bodyMessage;
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

function isLegacyError(payload: unknown): payload is { error: true; respBody?: unknown } {
  return !!payload && typeof payload === 'object' && (payload as { error?: unknown }).error === true;
}

export default function CoaVisibilityPage() {
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [activeLabel, setActiveLabel] = useState('');
  const [files, setFiles] = useState<CoaFileEntry[]>([]);
  const [printRuns, setPrintRuns] = useState<PrintRunEntry[]>([]);
  const [openPackageKeys, setOpenPackageKeys] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dropLanding, setDropLanding] = useState<CoaDropLanding | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CoaFileEntry | null>(null);
  const [addCoaTarget, setAddCoaTarget] = useState<CoaFileGroup | null>(null);
  const [addCoaFiles, setAddCoaFiles] = useState<File[]>([]);
  const [addCoaFileDates, setAddCoaFileDates] = useState<Record<string, string>>({});
  const [addCoaSaving, setAddCoaSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCoaFilesByLabel = async (packageLabel: string) => {
    const result = await getCoaFilesByLabel(packageLabel);
    if (isLegacyError(result)) {
      throw new Error(normalizeErrorBody(result.respBody) || 'Failed to retrieve COA files');
    }

    const nextFiles = Array.isArray(result) ? result : Array.isArray(result?.files) ? result.files : [];
    const nextPrintRuns = Array.isArray(result?.printRuns) ? result.printRuns : [];
    const nextGroups = groupCoaFiles(nextFiles, nextPrintRuns, packageLabel);

    setFiles(nextFiles);
    setPrintRuns(nextPrintRuns);
    setOpenPackageKeys(new Set(nextGroups.length > 1 ? [] : nextGroups.map((group) => group.key)));
    setActiveLabel(packageLabel);
    setSearched(true);
  };

  const handleSearch = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setFiles([]);
    setPrintRuns([]);
    setOpenPackageKeys(new Set());
    setSelected(new Set());
    setDropLanding(null);
    setDeleteTarget(null);
    setAddCoaTarget(null);
    setSearched(false);

    try {
      await loadCoaFilesByLabel(trimmed);
    } catch (nextError) {
      setError(readErrorMessage(nextError, 'Failed to retrieve COA files'));
    } finally {
      setLoading(false);
    }
  };

  const openAddCoaDialog = (group: CoaFileGroup) => {
    setAddCoaTarget(group);
    setAddCoaFiles([]);
    setAddCoaFileDates({});
  };

  const closeAddCoaDialog = () => {
    if (addCoaSaving) return;
    setAddCoaTarget(null);
    setAddCoaFiles([]);
    setAddCoaFileDates({});
  };

  const handleSelectAddCoaFiles = (selectedFiles: FileList | null) => {
    const nextFiles = selectedFiles ? Array.from(selectedFiles) : [];
    setAddCoaFiles(nextFiles);
    setAddCoaFileDates((prev) => {
      const nextDates: Record<string, string> = {};
      nextFiles.forEach((file) => {
        const key = pendingCoaFileKey(file);
        nextDates[key] = prev[key] || '';
      });
      return nextDates;
    });
  };

  const handleAddCoa = async () => {
    if (!addCoaTarget) return;

    const organizationId = Number(addCoaTarget.organizationId);
    const packageId = Number(addCoaTarget.packageId);
    if (!Number.isFinite(organizationId) || organizationId <= 0 || !Number.isFinite(packageId) || packageId <= 0) {
      showToastError('A package is required before adding a COA file.');
      return;
    }

    if (addCoaFiles.length === 0) {
      showToastError('Please choose at least one COA file.');
      return;
    }

    const missingDates = addCoaFiles.filter((file) => !addCoaFileDates[pendingCoaFileKey(file)]);
    if (missingDates.length > 0) {
      showToastError('Please provide a Test Performed Date for each COA file.');
      return;
    }

    setAddCoaSaving(true);
    const addedFileKeys = new Set<string>();
    const reloadLabel = activeLabel || addCoaTarget.packageLabel;

    try {
      for (const file of addCoaFiles) {
        const fileKey = pendingCoaFileKey(file);
        const fileId = await uploadFileId({
          organizationId,
          fileType: FileType.LabTesting,
          mimeType: file.type || 'application/pdf',
          file,
        });

        const result = await addCoaFile({
          organizationId,
          packageId,
          fileId: Number(fileId),
          testPerformedDate: addCoaFileDates[fileKey],
        });

        if (isLegacyError(result)) {
          throw new Error(normalizeErrorBody(result.respBody) || 'Failed to add COA file');
        }

        addedFileKeys.add(fileKey);
      }

      await loadCoaFilesByLabel(reloadLabel);
      setAddCoaTarget(null);
      setAddCoaFiles([]);
      setAddCoaFileDates({});
      showToastSuccess(`${addCoaFiles.length} COA file${addCoaFiles.length === 1 ? '' : 's'} added.`);
    } catch (nextError) {
      const errorMessage = readErrorMessage(nextError, 'Failed to add COA file');

      if (addedFileKeys.size > 0) {
        try {
          await loadCoaFilesByLabel(reloadLabel);
        } catch {
          // Keep the original add failure visible; the next search can recover table state.
        }

        setAddCoaFiles((prev) => prev.filter((file) => !addedFileKeys.has(pendingCoaFileKey(file))));
        setAddCoaFileDates((prev) => {
          const nextDates = { ...prev };
          addedFileKeys.forEach((fileKey) => delete nextDates[fileKey]);
          return nextDates;
        });
        showToastError(`${addedFileKeys.size} COA file${addedFileKeys.size === 1 ? '' : 's'} added before failure. ${errorMessage}`);
      } else {
        showToastError(errorMessage);
      }
    } finally {
      setAddCoaSaving(false);
    }
  };

  const toggleSelect = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectAllVisible = () => {
    const visibleKeys = files.filter((file) => !file.isHidden).map((file) => compositeKey(file));
    setSelected(new Set(visibleKeys));
  };

  const handleVisibilityChange = async (
    currentHidden: boolean,
    nextHidden: boolean,
    successAction: string,
  ) => {
    if (selected.size === 0) return;

    const requests = buildVisibilityRequests(files, selected, activeLabel, currentHidden, nextHidden);
    const requestedCount = requests.reduce((total, request) => total + request.fileEntries.length, 0);
    if (requestedCount === 0) return;

    const filesByKey = new Map(files.map((file) => [compositeKey(file), file]));
    const results: CoaFileMutationResult[] = [];

    setLoading(true);
    try {
      for (const request of requests) {
        try {
          const result = await setCoaFileVisibility(request);
          if (isLegacyError(result)) {
            throw new Error(normalizeErrorBody(result.respBody) || 'Failed to update visibility');
          }
          results.push(...extractMutationResults(result, request.fileEntries));
        } catch (nextError) {
          const reason = readErrorMessage(nextError, 'Failed to update visibility');
          results.push(...request.fileEntries.map((entry) => ({ ...entry, succeeded: false, reason })));
        }
      }

      const succeededKeys = new Set(
        results.filter((result) => result.succeeded).map((result) => mutationKey(result.id, result.isStaged)),
      );
      const failedResults = results.filter((result) => !result.succeeded);
      const failedKeys = new Set(failedResults.map((result) => mutationKey(result.id, result.isStaged)));

      setFiles((prev) =>
        prev.map((file) => (succeededKeys.has(compositeKey(file)) ? { ...file, isHidden: nextHidden } : file)),
      );
      setSelected((prev) => {
        const next = new Set(prev);
        succeededKeys.forEach((key) => next.delete(key));
        failedKeys.forEach((key) => next.add(key));
        return next;
      });

      const summary = mutationSummary(succeededKeys.size, failedResults, successAction, filesByKey);
      if (failedResults.length > 0) {
        showToastError(summary);
      } else {
        showToastSuccess(summary);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewFile = async (file: CoaFileEntry) => {
    if (!file.organizationId || !file.blobName) {
      showToastError('COA file link is not available for this row.');
      return;
    }

    const fileWindow = window.open('about:blank', '_blank');
    if (fileWindow) fileWindow.opener = null;

    try {
      const url = await getFileLink({
        organizationId: file.organizationId,
        fileType: FileType.LabTesting,
        name: file.blobName,
      });
      if (!url) throw new Error('COA file link is not available for this row.');

      if (fileWindow) {
        fileWindow.location.href = url;
      } else {
        showToastError('Browser blocked the COA PDF tab. Allow pop-ups for this site and try again.');
      }
    } catch (nextError) {
      if (fileWindow) fileWindow.close();
      showToastError(readErrorMessage(nextError, 'Failed to open COA PDF.'));
    }
  };

  const handleDeleteFile = async () => {
    if (!deleteTarget) return;

    setLoading(true);
    try {
      const packageLabel = packageLabelFor(deleteTarget, activeLabel);
      const result = await deleteCoaFile({
        packageLabel,
        fileEntries: [{ id: deleteTarget.id, isStaged: deleteTarget.isStaged }],
      });
      if (isLegacyError(result)) {
        throw new Error(normalizeErrorBody(result.respBody) || 'Failed to delete COA file');
      }

      const deletedKey = compositeKey(deleteTarget);
      const mutationResults = extractMutationResults(result, [{ id: deleteTarget.id, isStaged: deleteTarget.isStaged }]);
      const succeeded = mutationResults.some((entry) => entry.succeeded && mutationKey(entry.id, entry.isStaged) === deletedKey);
      const failedResult = mutationResults.find((entry) => !entry.succeeded && mutationKey(entry.id, entry.isStaged) === deletedKey);
      setDeleteTarget(null);

      if (!succeeded) {
        setSelected((prev) => new Set(prev).add(deletedKey));
        showToastError(
          mutationSummary(
            0,
            failedResult
              ? [failedResult]
              : [{ id: deleteTarget.id, isStaged: deleteTarget.isStaged, succeeded: false, reason: 'COA file was not deleted' }],
            'deleted',
            new Map([[deletedKey, deleteTarget]]),
          ),
        );
        return;
      }

      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(deletedKey);
        return next;
      });

      try {
        await loadCoaFilesByLabel(packageLabel);
      } catch {
        setFiles((prev) => prev.filter((file) => compositeKey(file) !== deletedKey));
        showToastError('1 COA deleted; positions could not be refreshed. Search again to reload the current order.');
        return;
      }

      showToastSuccess('COA file deleted.');
    } catch (nextError) {
      showToastError(readErrorMessage(nextError, 'Failed to delete COA file'));
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event: DragEvent<HTMLTableRowElement>, file: CoaFileEntry) => {
    if (file.isStaged || loading) return;

    const key = compositeKey(file);
    setDraggedKey(key);
    setDropLanding(null);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', key);
  };

  const handleDragOver = (
    event: DragEvent<HTMLTableRowElement>,
    group: CoaFileGroup,
    targetFile: CoaFileEntry,
  ) => {
    const sourceKey = draggedKey || event.dataTransfer.getData('text/plain');
    if (!sourceKey || targetFile.isStaged || loading) {
      setDropLanding(null);
      return;
    }

    const activeFiles = group.files.filter((file) => !file.isStaged);
    const sourceIndex = activeFiles.findIndex((file) => compositeKey(file) === sourceKey);
    const targetIndex = activeFiles.findIndex((file) => compositeKey(file) === compositeKey(targetFile));
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      setDropLanding(null);
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropLanding({
      groupKey: group.key,
      targetKey: compositeKey(targetFile),
      edge: sourceIndex < targetIndex ? 'after' : 'before',
    });
  };

  const saveCoaFileOrder = async (group: CoaFileGroup, orderedIds: number[]) => {
    const packageId = Number(group.packageId);
    if (!Number.isFinite(packageId) || packageId <= 0) {
      throw new Error('Package ID is required to reorder COA files.');
    }

    const result = await setCoaFileDisplayOrder({
      packageLabel: group.packageLabel,
      packageId,
      orderedIds,
    });

    if (isLegacyError(result)) {
      throw new Error(normalizeErrorBody(result.respBody) || 'Failed to update COA order');
    }

    const nextDisplayOrder = new Map(orderedIds.map((id, index) => [id, index + 1]));
    setFiles((prev) =>
      prev.map((file) =>
        packageGroupKey(file, activeLabel) === group.key && nextDisplayOrder.has(file.id)
          ? { ...file, displayOrder: nextDisplayOrder.get(file.id) }
          : file,
      ),
    );
    showToastSuccess('COA order saved.');
  };

  const handleDrop = async (
    event: DragEvent<HTMLTableRowElement>,
    group: CoaFileGroup,
    targetFile: CoaFileEntry,
  ) => {
    event.preventDefault();
    const sourceKey = draggedKey || event.dataTransfer.getData('text/plain');
    setDraggedKey(null);
    setDropLanding(null);

    if (!sourceKey || targetFile.isStaged || loading) return;

    const activeFiles = group.files.filter((file) => !file.isStaged);
    const sourceIndex = activeFiles.findIndex((file) => compositeKey(file) === sourceKey);
    const targetIndex = activeFiles.findIndex((file) => compositeKey(file) === compositeKey(targetFile));
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return;

    const reorderedFiles = [...activeFiles];
    const [movedFile] = reorderedFiles.splice(sourceIndex, 1);
    reorderedFiles.splice(targetIndex, 0, movedFile);

    setLoading(true);
    try {
      await saveCoaFileOrder(group, reorderedFiles.map((file) => file.id));
    } catch (nextError) {
      showToastError(readErrorMessage(nextError, 'Failed to update COA order'));
    } finally {
      setLoading(false);
    }
  };

  const handleMoveFile = async (group: CoaFileGroup, file: CoaFileEntry, direction: -1 | 1) => {
    if (file.isStaged || loading) return;

    const activeFiles = group.files.filter((groupFile) => !groupFile.isStaged);
    const sourceIndex = activeFiles.findIndex((groupFile) => compositeKey(groupFile) === compositeKey(file));
    const targetIndex = sourceIndex + direction;
    if (sourceIndex < 0 || targetIndex < 0 || targetIndex >= activeFiles.length) return;

    const reorderedFiles = [...activeFiles];
    const [movedFile] = reorderedFiles.splice(sourceIndex, 1);
    reorderedFiles.splice(targetIndex, 0, movedFile);

    setLoading(true);
    try {
      await saveCoaFileOrder(group, reorderedFiles.map((groupFile) => groupFile.id));
    } catch (nextError) {
      showToastError(readErrorMessage(nextError, 'Failed to update COA order'));
    } finally {
      setLoading(false);
    }
  };

  const selectedVisibleCount = files.filter((file) => selected.has(compositeKey(file)) && !file.isHidden).length;
  const selectedHiddenCount = files.filter((file) => selected.has(compositeKey(file)) && file.isHidden).length;
  const packageGroups = groupCoaFiles(files, printRuns, activeLabel);
  const hasMultiplePackageGroups = packageGroups.length > 1;

  return (
    <div className="stack" data-testid="page-coa-visibility">
      {addCoaTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add COA"
          data-testid="add-coa-dialog"
          onMouseDown={addCoaSaving ? undefined : closeAddCoaDialog}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            className="drawer-controls-card"
            onMouseDown={(event) => event.stopPropagation()}
            data-testid="add-coa-dialog-content"
            style={{ width: 440, maxWidth: 'calc(100vw - 32px)', alignSelf: 'center' }}
          >
            <div className="stack" style={{ gap: 12 }}>
              <div className="modal-title">Add COA</div>
              <div className="muted">{addCoaTarget.packageLabel}</div>
              <label className="stack" style={{ gap: 6 }}>
                <span>COA Files</span>
                <input
                  className="input"
                  type="file"
                  accept="application/pdf,image/*"
                  multiple
                  disabled={addCoaSaving}
                  onChange={(event) => handleSelectAddCoaFiles(event.target.files)}
                  data-testid="add-coa-file-input"
                />
              </label>
              {addCoaFiles.length > 0 && (
                <div className="stack" style={{ gap: 6 }}>
                  <span>Test Performed Date</span>
                  <div className="stack" style={{ gap: 6 }}>
                    {addCoaFiles.map((file) => {
                      const key = pendingCoaFileKey(file);
                      return (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                          <span style={{ minWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.name}
                          </span>
                          <input
                            className="input"
                            type="date"
                            value={addCoaFileDates[key] || ''}
                            disabled={addCoaSaving}
                            onChange={(event) =>
                              setAddCoaFileDates((prev) => ({ ...prev, [key]: event.target.value }))
                            }
                            data-testid={`add-coa-test-date-input-${key}`}
                            required
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="dialog-footer">
                <button className="button secondary" type="button" onClick={closeAddCoaDialog} disabled={addCoaSaving} data-testid="add-coa-cancel">
                  Cancel
                </button>
                <button className="button" type="button" onClick={handleAddCoa} disabled={addCoaSaving} data-testid="add-coa-save-btn">
                  {addCoaSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <FullScreenConfirmOverlay
        show={deleteTarget != null}
        title="Delete COA File"
        message={`Delete ${deleteTarget?.fileName || `file-${deleteTarget?.fileId}`} from this package? This cannot be undone.`}
        confirmLabel={loading ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        busy={loading}
        testId="confirm-delete-coa-file"
        onCancel={() => {
          if (loading) return;
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteFile}
      />

      <PageHeader
        title="COA Visibility"
        subtitle="Hide or restore COA files on package landing pages"
        actions={
          <button type="button" className="button secondary" onClick={() => navigate('/internal')} data-testid="coa-visibility-back">
            Back
          </button>
        }
      />

      <form className="card filter-card" onSubmit={handleSearch}>
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              type="search"
              placeholder="Search by package label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              disabled={loading}
              data-testid="coa-label-input"
            />
          </div>
          <button
            type="submit"
            className="button cta"
            disabled={loading || !label.trim()}
            data-testid="coa-search-btn"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {error && (
        <div className="alert error" data-testid="coa-visibility-error">
          {error}
        </div>
      )}

      {searched && files.length === 0 && printRuns.length === 0 && !error && (
        <div className="card" data-testid="coa-visibility-empty">
          <p className="muted">No COA files or print runs found for this package label.</p>
        </div>
      )}

      {(files.length > 0 || printRuns.length > 0) && (
        <div className="card" data-testid="coa-visibility-results">
          <div className="section-heading">
            <span className="muted">
              {files.length} COA file{files.length !== 1 ? 's' : ''} and {printRuns.length} print run{printRuns.length !== 1 ? 's' : ''} found
            </span>
            <div className="table-actions">
              {files.length > 0 && (
                <button
                  type="button"
                  className="button secondary"
                  onClick={selectAllVisible}
                  disabled={loading}
                  data-testid="coa-select-all-visible"
                >
                  Select All Visible
                </button>
              )}
              {selectedVisibleCount > 0 && (
                <button
                  type="button"
                  className="button"
                  onClick={() => handleVisibilityChange(false, true, 'hidden')}
                  disabled={loading}
                  data-testid="coa-hide-btn"
                >
                  Remove From Landing Page ({selectedVisibleCount})
                </button>
              )}
              {selectedHiddenCount > 0 && (
                <button
                  type="button"
                  className="button cta"
                  onClick={() => handleVisibilityChange(true, false, 'restored')}
                  disabled={loading}
                  data-testid="coa-restore-btn"
                >
                  Restore to Landing Page ({selectedHiddenCount})
                </button>
              )}
            </div>
          </div>

          <div className="stack">
            {packageGroups.map((group) => {
              const activeFiles = group.files.filter((file) => !file.isStaged);
              const activeFileIndexByKey = new Map(activeFiles.map((file, index) => [compositeKey(file), index]));

              return (
                <details
                  className="card package-group-card"
                  key={group.key}
                  open={openPackageKeys.has(group.key)}
                  data-testid={`coa-group-${group.key}`}
                  data-open={openPackageKeys.has(group.key)}
                  onToggle={(event) => {
                    const isOpen = event.currentTarget.open;
                    setOpenPackageKeys((prev) => {
                      const next = new Set(prev);
                      if (isOpen) {
                        next.add(group.key);
                      } else {
                        next.delete(group.key);
                      }
                      return next;
                    });
                  }}
                >
                  <summary className="section-heading package-group-summary" data-testid={`coa-group-summary-${group.key}`}>
                    <span>
                      {hasMultiplePackageGroups && <span className="disclosure-arrow" aria-hidden="true" />}
                      {group.packageLabel}
                      {group.packageId != null && (
                        <span className="muted"> Package ID {group.packageId}</span>
                      )}
                    </span>
                    <span className="muted">
                      {group.files.length} COA file{group.files.length !== 1 ? 's' : ''}
                      {group.printRuns.length > 0 && `, ${group.printRuns.length} print run${group.printRuns.length !== 1 ? 's' : ''}`}
                    </span>
                    {group.printRuns.length > 0 && (
                      <button
                        type="button"
                        className="button secondary"
                        onClick={(event) => {
                          event.preventDefault();
                          openAddCoaDialog(group);
                        }}
                        disabled={loading || !group.packageId || !group.organizationId}
                        data-testid={`coa-group-add-${group.key}`}
                      >
                        Add COA
                      </button>
                    )}
                  </summary>

                  {group.printRuns.length > 0 && (
                    <div className="stack">
                      <div className="section-heading">
                        <span className="muted">Print Runs</span>
                      </div>
                      <table className="table" data-testid={`coa-group-print-runs-${group.key}`}>
                        <thead>
                          <tr>
                            <th>Print Run</th>
                            <th>Range</th>
                            <th>Organization</th>
                            <th>License</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.printRuns.map((printRun) => (
                            <tr key={printRun.id} data-testid={`coa-group-${group.key}-print-run-${printRun.id}`}>
                              <td>
                                {printRun.jobName || printRun.jobId || `#${printRun.id}`}
                                {(printRun.jobName || printRun.jobId) && (
                                  <span className="muted"> #{printRun.id}</span>
                                )}
                              </td>
                              <td>{formatPrintRunRange(printRun.range) || 'Not available'}</td>
                              <td>{printRun.organizationName || printRun.organizationId || 'Not available'}</td>
                              <td>{printRun.facilityLicense || 'Not available'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {group.files.length > 0 ? (
                    <table className="table" data-testid={`coa-group-files-${group.key}`}>
                      <thead>
                        <tr>
                          <th aria-label="select file" />
                          <th>Position</th>
                          <th>File Name</th>
                          <th>Test Date</th>
                          <th>Source</th>
                          <th>Status</th>
                          <th className="table-actions-cell">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.files.map((file) => {
                          const activeIndex = activeFileIndexByKey.get(compositeKey(file)) ?? -1;
                          const canMove = !file.isStaged && activeIndex >= 0;

                          return (
                            <tr
                              key={compositeKey(file)}
                              data-testid={`coa-file-row-${compositeKey(file)}`}
                              data-selected={selected.has(compositeKey(file))}
                              data-hidden={file.isHidden}
                              draggable={!loading && !file.isStaged}
                              onDragStart={(event) => handleDragStart(event, file)}
                              onDragOver={(event) => handleDragOver(event, group, file)}
                              onDrop={(event) => handleDrop(event, group, file)}
                              onDragEnd={() => {
                                setDraggedKey(null);
                                setDropLanding(null);
                              }}
                              aria-grabbed={draggedKey === compositeKey(file)}
                              title={file.isStaged ? undefined : 'Drag to reorder COA files'}
                              style={{
                                ...(file.isHidden ? { opacity: 0.6 } : {}),
                                ...(!file.isStaged ? { cursor: 'grab' } : {}),
                                ...(dropLanding?.groupKey === group.key &&
                                dropLanding.targetKey === compositeKey(file) &&
                                dropLanding.edge === 'before'
                                  ? { boxShadow: 'inset 0 3px 0 var(--color-primary, #2563eb)' }
                                  : {}),
                                ...(dropLanding?.groupKey === group.key &&
                                dropLanding.targetKey === compositeKey(file) &&
                                dropLanding.edge === 'after'
                                  ? { boxShadow: 'inset 0 -3px 0 var(--color-primary, #2563eb)' }
                                  : {}),
                              }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selected.has(compositeKey(file))}
                                  onChange={() => toggleSelect(compositeKey(file))}
                                  disabled={loading}
                                  aria-label={`Select ${file.fileName || 'file'}`}
                                  data-testid={`coa-file-select-${compositeKey(file)}`}
                                />
                              </td>
                              <td>{file.displayOrder ?? '-'}</td>
                              <td>
                                <button
                                  type="button"
                                  className="text-link"
                                  onClick={() => handleViewFile(file)}
                                  disabled={loading || !file.organizationId || !file.blobName}
                                  title="Open COA PDF"
                                  aria-label={`Open ${file.fileName || 'COA file'}`}
                                  data-testid={`coa-view-${compositeKey(file)}`}
                                >
                                  {file.fileName || `file-${file.fileId}`}
                                </button>
                              </td>
                              <td>
                                {formatDate(file.testPerformedDate) || '-'}
                              </td>
                              <td>
                                <span className="tag subtle">{file.isStaged ? 'Staged' : 'Active'}</span>
                              </td>
                              <td>
                                {file.isHidden ? (
                                  <span className="status-archived">Hidden</span>
                                ) : (
                                  <span className="status-active">Visible</span>
                                )}
                              </td>
                              <td className="table-actions-cell">
                                <div className="row-action-icons">
                                  <IconButton
                                    title="Move COA file up"
                                    ariaLabel={`Move ${file.fileName || 'COA file'} up`}
                                    onClick={() => handleMoveFile(group, file, -1)}
                                    disabled={loading || !canMove || activeIndex === 0}
                                    testId={`coa-move-up-${compositeKey(file)}`}
                                  >
                                    <span aria-hidden="true">↑</span>
                                  </IconButton>
                                  <IconButton
                                    title="Move COA file down"
                                    ariaLabel={`Move ${file.fileName || 'COA file'} down`}
                                    onClick={() => handleMoveFile(group, file, 1)}
                                    disabled={loading || !canMove || activeIndex === activeFiles.length - 1}
                                    testId={`coa-move-down-${compositeKey(file)}`}
                                  >
                                    <span aria-hidden="true">↓</span>
                                  </IconButton>
                                  <IconButton
                                    title="Delete COA file"
                                    ariaLabel={`Delete ${file.fileName || 'COA file'}`}
                                    onClick={() => setDeleteTarget(file)}
                                    disabled={loading}
                                    testId={`coa-delete-${compositeKey(file)}`}
                                  >
                                    <TrashActionIcon />
                                  </IconButton>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p className="muted" data-testid={`coa-group-files-empty-${group.key}`}>No COA files found for this package group.</p>
                  )}
                </details>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
